import axios from "axios";
import { buildOfficialReceiptErrorPayload, buildOfficialReceiptPayload, emitOfficialReceipt, shouldOpenOfficialReceipt } from "./transactionReceipt";

// Production-safe API URL.
// If REACT_APP_BACKEND_URL is missing during the frontend build, use the
// current website origin instead of generating requests such as
// "undefined/api/auth/login".
const RAW_BACKEND_URL = String(
    process.env.REACT_APP_BACKEND_URL || ""
).trim();

const BACKEND_URL = RAW_BACKEND_URL.replace(/\/+$/, "");

export const API = BACKEND_URL
    ? `${BACKEND_URL}/api`
    : "/api";

export const api = axios.create({
    baseURL: API,
    // Pa kite yon API/provider ki pa reponn bloke PWA a san limit.
    timeout: 20000,
});

export function resolveAssetUrl(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";

    // Keep browser-safe absolute/data/blob URLs untouched.
    if (/^(?:https?:|data:|blob:)/i.test(raw)) return raw;

    // Files uploaded by the backend are returned as /uploads/... .
    // When frontend and backend use different preview hosts, point them
    // to REACT_APP_BACKEND_URL instead of the frontend origin.
    if (raw.startsWith("/uploads/")) {
        // Existing design records may contain the legacy /uploads path.
        // In preview deployments only /api is guaranteed to proxy to FastAPI.
        return BACKEND_URL ? `${BACKEND_URL}${raw}` : `/api${raw}`;
    }

    if (raw.startsWith("/")) {
        return BACKEND_URL ? `${BACKEND_URL}${raw}` : raw;
    }

    return raw;
}

// API izole pou mini aplikasyon WhatsApp Support la. Li itilize yon token
// apa pou koneksyon Support lan pa ranplase sesyon MKTRANSFE prensipal la.
export const supportApi = axios.create({
    baseURL: API,
    timeout: 15000,
});

supportApi.interceptors.request.use((config) => {
    const token = localStorage.getItem("mk_support_token");

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("mk_token");

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

// Reject proxy error pages returned with HTTP 200 before they reach dashboards.
// An HTML response is never valid for an API JSON request.
api.interceptors.response.use((response) => {
    const type = String(response?.headers?.["content-type"] || "").toLowerCase();
    const body = response?.data;
    const looksLikeHtml = typeof body === "string" && /^\s*(?:<!doctype html|<html|<head|<body)/i.test(body);
    if (looksLikeHtml || (type.includes("text/html") && typeof body === "string")) {
        const error = new Error("Unexpected HTML response from API");
        error.code = "ERR_BAD_RESPONSE";
        error.config = response.config;
        error.response = { ...response, status: 502 };
        return Promise.reject(error);
    }
    return response;
});

// A single safe retry for transient READ failures. Financial mutations are
// never retried automatically, so duplicate payments/transfers cannot be
// created by this protection.
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const config = error?.config;
        if (!config) return Promise.reject(error);

        const method = String(config.method || "get").toLowerCase();
        const status = Number(error?.response?.status || 0);
        const transient =
            !error?.response ||
            status === 408 ||
            status === 425 ||
            status === 429 ||
            status >= 500;
        const safeMethod = method === "get" || method === "head";
        const cancelled = error?.code === "ERR_CANCELED";
        const retryCount = Number(config.__mkRetryCount || 0);

        if (
            transient &&
            safeMethod &&
            !cancelled &&
            config.noRetry !== true &&
            retryCount < 1
        ) {
            config.__mkRetryCount = retryCount + 1;
            await new Promise((resolve) => setTimeout(resolve, 450));
            return api(config);
        }

        return Promise.reject(error);
    },
);

export function isTransientApiError(error) {
    const status = Number(error?.response?.status || 0);
    if (error?.code === "ERR_CANCELED") return false;
    if (!error?.response) return true;
    return status === 408 || status === 425 || status === 429 || status >= 500;
}

api.interceptors.response.use(
    (response) => {
        try {
            if (shouldOpenOfficialReceipt(response?.config)) {
                emitOfficialReceipt(buildOfficialReceiptPayload(response));
            }
        } catch (error) {
            // Yon pwoblèm nan UI resi a pa dwe janm bloke operasyon finansye a.
            console.warn("Official receipt event skipped:", error);
        }
        return response;
    },
    (error) => {
        try {
            const failedReceipt = buildOfficialReceiptErrorPayload(error);
            if (failedReceipt) {
                emitOfficialReceipt(failedReceipt);
            }
        } catch (receiptError) {
            console.warn("Failed official receipt event skipped:", receiptError);
        }
        return Promise.reject(error);
    }
);

export function fmtHTG(n) {
    if (
        n === null ||
        n === undefined ||
        isNaN(n)
    ) {
        return "0.00";
    }

    return Number(n).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}


// Currency label per country: RD->DOP, HT->HTG (Goud)
export function currencyOf(country) {
    return country === "HT"
        ? "HTG"
        : "DOP";
}


export function compressImageToBase64(
    file,
    maxBytes = 1_800_000
) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();

        reader.onload = (e) => {
            img.onload = () => {
                let {
                    width,
                    height,
                } = img;

                const MAX_DIM = 1600;

                if (
                    width > MAX_DIM ||
                    height > MAX_DIM
                ) {
                    const ratio = Math.min(
                        MAX_DIM / width,
                        MAX_DIM / height
                    );

                    width = Math.round(
                        width * ratio
                    );

                    height = Math.round(
                        height * ratio
                    );
                }

                const canvas =
                    document.createElement("canvas");

                canvas.width = width;
                canvas.height = height;

                const ctx =
                    canvas.getContext("2d");

                ctx.drawImage(
                    img,
                    0,
                    0,
                    width,
                    height
                );

                let q = 0.82;

                let out =
                    canvas.toDataURL(
                        "image/jpeg",
                        q
                    );

                while (
                    out.length > maxBytes &&
                    q > 0.3
                ) {
                    q -= 0.1;

                    out =
                        canvas.toDataURL(
                            "image/jpeg",
                            q
                        );
                }

                resolve(out);
            };

            img.onerror = reject;
            img.src = e.target.result;
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}


// Accepts image (jpg/png) OR pdf.
// Max 2MB. Returns base64 data URL.
export function readReceiptFile(
    file,
    maxBytes = 2_097_152
) {
    return new Promise((resolve, reject) => {
        if (!file) {
            reject(
                new Error(
                    "Pa gen fichye"
                )
            );
            return;
        }

        if (file.size > maxBytes) {
            reject(
                new Error(
                    "Fichye a depase 2 MB"
                )
            );
            return;
        }

        const ok = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "application/pdf",
        ];

        if (!ok.includes(file.type)) {
            reject(
                new Error(
                    "Fòma pa sipòte. Itilize JPG, PNG oswa PDF."
                )
            );
            return;
        }

        if (
            file.type ===
            "application/pdf"
        ) {
            const reader =
                new FileReader();

            reader.onload = (e) =>
                resolve(
                    e.target.result
                );

            reader.onerror = reject;

            reader.readAsDataURL(file);

        } else {
            compressImageToBase64(
                file,
                maxBytes
            )
                .then(resolve)
                .catch(reject);
        }
    });
}


export function timeGreeting() {
    const h =
        new Date().getHours();

    if (
        h >= 6 &&
        h < 12
    ) {
        return "Bonjour";
    }

    if (
        h >= 12 &&
        h < 18
    ) {
        return "Bon après-midi";
    }

    return "Bonsoir";
}


// Calculate MonCash/NatCash service carrier fee
// Mirrors backend logic.
export function calcServiceFee(
    service,
    amountHtg
) {
    const MONCASH = [
        [0, 249, 0],
        [250, 499, 5],
        [500, 999, 10],
        [1000, 1999, 25],
        [2000, 3999, 35],
        [4000, 7999, 50],
        [8000, 11999, 60],
        [12000, 19999, 70],
        [20000, 39999, 75],
        [40000, 59999, 100],
        [60000, 74999, 120],
        [75000, 100000, 130],
    ];

    const NATCASH = [
        [20, 499, 0],
        [500, 999, 6],
        [1000, 1999, 18],
        [2000, 3999, 25],
        [4000, 7999, 35],
        [8000, 11999, 54],
        [12000, 19999, 63],
        [20000, 40000, 68],
        [40001, 59999, 90],
        [60000, 74999, 108],
        [75000, 99999, 115],
    ];

    const tiers =
        service === "MonCash"
            ? MONCASH
            : NATCASH;

    const a =
        Number(amountHtg) || 0;

    for (
        const [
            lo,
            hi,
            fee,
        ] of tiers
    ) {
        if (
            a >= lo &&
            a <= hi
        ) {
            return fee;
        }
    }

    return tiers[
        tiers.length - 1
    ][2];
}


export function fmtGourdes(n) {
    if (
        n === null ||
        n === undefined ||
        isNaN(n)
    ) {
        return "0.00";
    }

    return Number(n).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}


export function feeRateFor(amount) {
    const a =
        Number(amount) || 0;

    if (a < 4000) {
        return 0.1;
    }

    if (a < 8000) {
        return 0.07;
    }

    return 0.05;
}


export function fmtDate(iso) {
    if (!iso) {
        return "—";
    }

    try {
        const d =
            new Date(iso);

        return d.toLocaleString(
            "fr-FR",
            {
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            }
        );

    } catch {
        return iso;
    }
}


// =====================================================
// GLOBAL SAFE ERROR MESSAGE
// Pa ekspoze raw API/provider errors sou frontend.
// =====================================================
export function errMsg(
    err,
    fallback =
        "Nou pa kapab trete demann sa a pou moman an. Tanpri eseye ankò pita."
) {
    const status =
        Number(
            err?.response?.status || 0
        );

    const detail =
        err?.response?.data?.detail;

    const message =
        err?.response?.data?.message;

    // =================================================
    // SERVER / PROVIDER ERRORS
    // Pa montre okenn detay teknik.
    // =================================================
    if (status >= 500) {
        return fallback;
    }

    // =================================================
    // NETWORK / TIMEOUT
    // =================================================
    if (
        err?.code === "ERR_CANCELED" ||
        err?.code === "ETIMEDOUT" ||
        err?.code ===
            "ECONNABORTED" ||
        err?.code ===
            "ERR_NETWORK" ||
        err?.message ===
            "Network Error"
    ) {
        return (
            "Nou pa kapab konekte ak sèvis la pou moman an. " +
            "Tanpri eseye ankò pita."
        );
    }

    // =================================================
    // FASTAPI VALIDATION ERRORS
    // =================================================
    if (Array.isArray(detail)) {
        const messages =
            detail
                .map((item) => {
                    if (
                        item &&
                        typeof item.msg ===
                            "string"
                    ) {
                        return item.msg;
                    }

                    return "";
                })
                .filter(Boolean);

        if (messages.length) {
            return messages.join(" ");
        }

        return fallback;
    }

    // =================================================
    // BACKEND BUSINESS MESSAGE
    // =================================================
    if (
        typeof detail ===
        "string"
    ) {
        const clean =
            detail
                .replace(
                    /^[A-Z_]+:/,
                    ""
                )
                .trim();

        if (
            !isTechnicalApiMessage(
                clean
            )
        ) {
            return (
                clean ||
                fallback
            );
        }

        return fallback;
    }

    // =================================================
    // ALTERNATIVE MESSAGE FIELD
    // =================================================
    if (
        typeof message ===
        "string"
    ) {
        const clean =
            message.trim();

        if (
            !isTechnicalApiMessage(
                clean
            )
        ) {
            return (
                clean ||
                fallback
            );
        }

        return fallback;
    }

    return fallback;
}


// =====================================================
// DETECT RAW / TECHNICAL API MESSAGE
// =====================================================
function isTechnicalApiMessage(
    value
) {
    const text =
        String(value || "").trim();

    if (!text) {
        return false;
    }

    const technicalPatterns = [
        /status\s*:/i,
        /body\s*:/i,
        /response\s*body/i,
        /request\s*body/i,
        /traceback/i,
        /stack\s*trace/i,
        /internal\s*server\s*error/i,
        /api\s*error/i,
        /provider\s*error/i,
        /provider\s*response/i,
        /http\s*\d{3}/i,
        /httpx/i,
        /axios/i,
        /exception/i,
        /uvicorn/i,
        /fastapi/i,
        /deno/i,
        /cloudflare/i,
        /imunify/i,
        /localhost/i,
        /127\.0\.0\.1/i,
        /\/v\d+\//i,
        /\{"error"/i,
        /\{"detail"/i,
        /invalid_amount/i,
        /insufficient_balance/i,
        /duplicate_reference/i,
        /authorization/i,
        /bearer\s+/i,
        /x-mk-api/i,
        /x-mcc/i,
    ];

    return technicalPatterns.some(
        (pattern) =>
            pattern.test(text)
    );
}


// =====================================================
// ERROR CODE
// Kenbe pou lojik entèn frontend lan.
// =====================================================
export function errCode(err) {
    const d =
        err?.response?.data?.detail;

    if (
        typeof d ===
        "string"
    ) {
        const m =
            d.match(
                /^([A-Z_]+):/
            );

        if (m) {
            return m[1];
        }
    }

    return "";
}